import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X, Settings, Plus, Pencil, Network, Copy, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSidebar } from './Sidebar';
import { cn } from '../../utils/cn';
import { useAuth } from '../../stores/AuthContext';
import { getUserDashboardState, upsertUserDashboardState } from '../../services/userDashboardState';
import CacheSettings from '../../shared/CacheSettings/CacheSettings';
import HideInWorkshop from '../common/HideInWorkshop';
import SidebarAccountFooter from './SidebarAccountFooter';
import WarmupStatusIndicator from '../common/WarmupStatusIndicator';
import WarmupDebugPanel from '../common/WarmupDebugPanel'; // DEBUG(temp): remove alongside the debug endpoint once cache warming is fixed
import { useWorkshopMode } from '../../stores/WorkshopModeContext';
import { Input } from '../ui/input';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import TabFilterModal from '../../features/filter/TabFilterModal/TabFilterModal';
import { hasActiveFilters } from '../../stores/GlobalFilterContext/TabFilterOverrideContext';

// A single draggable/sortable tab row. Extracted so useSortable (a hook) has
// a real component to attach to — it cannot be called from inside the
// .map() callback in AppSidebar itself (that would violate the Rules of
// Hooks: the number of hook calls would change whenever a tab is added or
// removed). The drag handle is a small dedicated element, never the whole
// row or the tab-switch button, so it never conflicts with the existing
// click-to-switch, double-click-to-rename, or hover-reveal-icon interactions.
function TabRow({
  tab,
  isActive,
  isOnlyTab,
  isEditing,
  editValue,
  setEditValue,
  inputRef,
  onTabChange,
  startEditing,
  commitEdit,
  handleEditKeyDown,
  onRemoveTab,
  onToggleProcessGraph,
  onOpenTabFilter,
  onDuplicateTab,
  workshopMode,
  t,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tab.id,
    disabled: workshopMode,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const tabHasFilters = tab.tabFilters && hasActiveFilters(tab.tabFilters);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('group relative flex items-center', isDragging && 'opacity-50 z-10')}
    >
      {/* Drag handle — always visible (never HideInWorkshop-wrapped) so Workshop
          Mode reads as "disabled", not as a feature that silently vanished. */}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              {...(workshopMode ? {} : { ...attributes, ...listeners })}
              aria-disabled={workshopMode}
              aria-label={t('sidebar.dragHandle', { label: tab.label })}
              className={cn(
                'flex items-center justify-center w-4 h-8 flex-shrink-0 rounded touch-none mr-0.5',
                workshopMode
                  ? 'text-gray-200 cursor-not-allowed'
                  : 'text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity',
              )}
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {workshopMode ? t('sidebar.workshopModeRestricted') : t('sidebar.dragHandle', { label: tab.label })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <button
        onClick={() => !isEditing && onTabChange(tab.id)}
        onDoubleClick={(e) => startEditing(tab, e)}
        title={tabHasFilters ? `${tab.label} — Tab-Filter aktiv` : tab.label}
        className={cn(
          'flex-1 flex items-center justify-start h-10 px-3 rounded-md text-sm font-medium transition-colors min-w-0',
          'focus:outline-none focus:ring-2 focus:ring-primary',
          isActive
            ? 'bg-primary text-primary-foreground hover:bg-primary'
            : tabHasFilters
              ? 'text-gray-700 bg-blue-50 ring-1 ring-inset ring-blue-200 hover:bg-blue-100'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
          // The duplicate-tab icon is always visible (unlike the other 4, which
          // are HideInWorkshop-wrapped), so a minimum slot is always reserved.
          workshopMode ? 'pr-6' : (isOnlyTab ? 'pr-[84px]' : 'pr-[104px]'),
        )}
      >
        {isEditing ? (
          <Input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleEditKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="h-6 py-0 px-1 text-sm text-gray-900 bg-white border-primary"
          />
        ) : (
          <span className="truncate">{tab.label}</span>
        )}
        {!isEditing && tabHasFilters && (
          <span
            className={cn(
              'ml-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full',
              isActive ? 'bg-white' : 'bg-blue-500',
            )}
          />
        )}
        {!isEditing && tab.badge && (
          <span className={cn(
            'ml-auto px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0',
            isActive
              ? 'bg-primary-foreground/20 text-primary-foreground'
              : 'bg-gray-100 text-gray-600',
          )}>
            {tab.badge}
          </span>
        )}
      </button>

      {/* Duplicate button — always visible (never HideInWorkshop-wrapped): in
          Workshop Mode it stays present but dimmed + inert + tooltip-explained,
          rather than silently vanishing like the other 4 icons below. */}
      {!isEditing && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!workshopMode) onDuplicateTab?.(tab.id);
                }}
                disabled={workshopMode}
                aria-disabled={workshopMode}
                className={cn(
                  'absolute flex items-center justify-center w-5 h-5 rounded right-1',
                  workshopMode
                    ? 'text-gray-200 cursor-not-allowed opacity-100'
                    : 'opacity-0 group-hover:opacity-60 hover:!opacity-100 text-gray-400 hover:text-primary transition-opacity',
                  'focus:outline-none',
                )}
                aria-label={t('sidebar.duplicateTab', { label: tab.label })}
              >
                <Copy className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {workshopMode ? t('sidebar.workshopModeRestricted') : t('sidebar.duplicateTab', { label: tab.label })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Pencil/rename button — all tabs are editable */}
      {!isEditing && (
        <HideInWorkshop>
          <button
            onClick={(e) => startEditing(tab, e)}
            className={cn(
              'absolute flex items-center justify-center w-5 h-5 rounded',
              isOnlyTab ? 'right-11' : 'right-16',
              'opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity',
              'text-gray-400 hover:text-primary',
              'focus:outline-none',
            )}
            aria-label={`Rename ${tab.label}`}
            title={t('sidebar.renameTab')}
          >
            <Pencil className="h-3 w-3" />
          </button>
        </HideInWorkshop>
      )}

      {/* Delete button — hidden when this is the last remaining tab */}
      {!isOnlyTab && !isEditing && (
        <HideInWorkshop>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveTab?.(tab.id);
            }}
            className={cn(
              'absolute right-11 flex items-center justify-center w-5 h-5 rounded',
              'opacity-0 group-hover:opacity-100 transition-opacity',
              'text-gray-400 hover:text-red-500 hover:bg-red-50',
              'focus:outline-none focus:ring-2 focus:ring-red-400',
            )}
            aria-label={`Remove ${tab.label}`}
            title={`Remove ${tab.label}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </HideInWorkshop>
      )}

      {/* Process graph toggle — all tabs */}
      {!isEditing && (
        <HideInWorkshop>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleProcessGraph?.(tab.id);
            }}
            className={cn(
              'absolute flex items-center justify-center w-5 h-5 rounded',
              'right-6',
              'opacity-0 group-hover:opacity-100 transition-opacity',
              tab.showProcessGraph
                ? 'text-primary hover:text-primary/80'
                : 'text-gray-300 hover:text-gray-500',
              'focus:outline-none',
            )}
            title={tab.showProcessGraph ? 'Prozessgraph ausblenden' : 'Prozessgraph einblenden'}
            aria-label={tab.showProcessGraph ? `Hide process graph on ${tab.label}` : `Show process graph on ${tab.label}`}
          >
            <Network className="h-3 w-3" />
          </button>
        </HideInWorkshop>
      )}

      {/* Tab filter settings — all tabs */}
      {!isEditing && (
        <HideInWorkshop>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenTabFilter(tab);
            }}
            className={cn(
              'absolute flex items-center justify-center w-5 h-5 rounded',
              isOnlyTab ? 'right-16' : 'right-[84px]',
              'opacity-0 group-hover:opacity-100 transition-opacity',
              // On the highlighted (active) tab the row background is bg-primary,
              // so a text-primary icon would be invisible — use a light/white tone instead.
              isActive
                ? (tabHasFilters ? 'text-white hover:text-white/80' : 'text-primary-foreground/50 hover:text-primary-foreground/80')
                : (tabHasFilters ? 'text-primary hover:text-primary/80' : 'text-gray-300 hover:text-gray-500'),
              'focus:outline-none',
            )}
            title="Tab-Filter"
            aria-label={`Filter für ${tab.label} konfigurieren`}
          >
            <Settings className="h-3 w-3" />
            {tabHasFilters && (
              <span className={cn(
                'absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full',
                isActive ? 'bg-white' : 'bg-blue-500',
              )} />
            )}
          </button>
        </HideInWorkshop>
      )}
    </div>
  );
}

const AppSidebar = ({ activeTab, onTabChange, tabs = [], activeFile, onCreateTab, onRemoveTab, onRenameTab, onToggleProcessGraph, onUpdateTabFilters, onDuplicateTab, onReorderTabs, startEditingTabId }) => {
  const { t } = useTranslation('common');
  const { state, toggleSidebar } = useSidebar();
  const { user, logout } = useAuth();
  const { workshopMode } = useWorkshopMode();
  const [showCacheSettings, setShowCacheSettings] = useState(false);
  const [autoActivateLatest, setAutoActivateLatest] = useState(false);
  const [filterModalTab, setFilterModalTab] = useState(null);
  const [editingTabId, setEditingTabId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);
  const sidebarRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const isCollapsed = state === 'collapsed';

  useEffect(() => {
    if (isCollapsed) return;
    const handleClickOutside = (e) => {
      if (sidebarRef.current?.contains(e.target) || toggleBtnRef.current?.contains(e.target)) return;
      toggleSidebar();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCollapsed, toggleSidebar]);

  // Normalize tabs structure
  const normalizedTabs = tabs.map(tab => ({
    ...tab,
    id: tab.id || tab,
    label: tab.label || tab.title || tab,
  }));

  // Load this user's "always use latest scheduled data" preference
  useEffect(() => {
    if (!user?.id) return;
    getUserDashboardState()
      .then((state) => setAutoActivateLatest(!!state?.auto_activate_latest_etl_file))
      .catch(() => {});
  }, [user?.id]);

  const handleToggleAutoActivate = (checked) => {
    setAutoActivateLatest(checked);
    upsertUserDashboardState({ auto_activate_latest_etl_file: checked }).catch(() => {
      setAutoActivateLatest(!checked);
    });
  };

  // Focus input when edit mode activates
  useEffect(() => {
    if (editingTabId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingTabId]);

  // Auto-enter edit mode when a new tab is created
  useEffect(() => {
    if (!startEditingTabId) return;
    const tab = normalizedTabs.find(t => t.id === startEditingTabId);
    if (tab) {
      setEditingTabId(startEditingTabId);
      setEditValue(tab.label);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startEditingTabId]);

  const startEditing = (tab, e) => {
    e.stopPropagation();
    setEditingTabId(tab.id);
    setEditValue(tab.label);
  };

  const commitEdit = () => {
    if (editingTabId) {
      onRenameTab?.(editingTabId, editValue);
    }
    setEditingTabId(null);
  };

  const cancelEdit = () => {
    setEditingTabId(null);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  // Drag-and-drop tab reordering (dnd-kit). A small distance threshold keeps
  // ordinary clicks from being hijacked as drags; the keyboard sensor covers
  // the case where a pointer isn't available.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [activeDragId, setActiveDragId] = useState(null);

  // dnd-kit can't force-cancel a gesture whose pointer is already captured by
  // the browser just because a prop changed — this only clears our own
  // DragOverlay visual if Workshop Mode turns on mid-drag. The actual commit
  // is still blocked by handleReorderTabs's own guard in App.jsx regardless
  // of how the in-progress gesture resolves, which is why that guard exists
  // in addition to disabling the sensor here.
  useEffect(() => {
    if (workshopMode && activeDragId) setActiveDragId(null);
  }, [workshopMode, activeDragId]);

  const handleDragEnd = (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tabs.findIndex((tb) => (tb.id || tb) === active.id);
    const newIndex = tabs.findIndex((tb) => (tb.id || tb) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorderTabs?.(arrayMove(tabs, oldIndex, newIndex));
  };

  return (
    <>
      <div
        ref={sidebarRef}
        className={cn(
          'h-full transition-all duration-300 overflow-hidden flex-shrink-0',
          isCollapsed ? 'w-0' : 'w-64',
          isCollapsed ? '' : 'bg-white shadow-lg border-r border-gray-200',
        )}
      >
        <div className={cn(
          'transition-opacity duration-300 h-full w-64 flex flex-col',
          isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100',
        )}>
          {/* Header */}
          <div className="border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-center px-4 py-4">
              <div className="w-full flex justify-center">
                <img
                  src="/shape_Logo_final_Designversionen_012_blue.svg"
                  alt="FIR"
                  className="h-12 w-auto max-w-full transition-all duration-200"
                />
              </div>
            </div>
          </div>

          {/* Navigation Content */}
          <div className="flex-1 px-2 py-2 overflow-y-auto">
            <nav className="space-y-1">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(e) => !workshopMode && setActiveDragId(e.active.id)}
                onDragEnd={handleDragEnd}
                onDragCancel={() => setActiveDragId(null)}
              >
                <SortableContext
                  items={normalizedTabs.map((tab) => tab.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {normalizedTabs.map((tab) => (
                    <TabRow
                      key={tab.id}
                      tab={tab}
                      isActive={activeTab === tab.id}
                      // At least one tab must always exist — removal is blocked only when
                      // this is the last remaining tab, regardless of which tab it is.
                      isOnlyTab={normalizedTabs.length <= 1}
                      isEditing={editingTabId === tab.id}
                      editValue={editValue}
                      setEditValue={setEditValue}
                      inputRef={inputRef}
                      onTabChange={onTabChange}
                      startEditing={startEditing}
                      commitEdit={commitEdit}
                      handleEditKeyDown={handleEditKeyDown}
                      onRemoveTab={onRemoveTab}
                      onToggleProcessGraph={onToggleProcessGraph}
                      onOpenTabFilter={setFilterModalTab}
                      onDuplicateTab={onDuplicateTab}
                      workshopMode={workshopMode}
                      t={t}
                    />
                  ))}
                </SortableContext>
                <DragOverlay>
                  {activeDragId ? (
                    <div className="flex items-center h-10 px-3 rounded-md text-sm font-medium bg-primary text-primary-foreground shadow-lg opacity-90">
                      {normalizedTabs.find((t2) => t2.id === activeDragId)?.label}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>

              {onCreateTab && (
                <HideInWorkshop>
                  <button
                    onClick={onCreateTab}
                    className="w-full flex items-center justify-start h-10 px-3 rounded-md text-sm font-medium
                               text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors border border-dashed border-gray-200 mt-2"
                  >
                    <Plus className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="truncate">{t('sidebar.newTab')}</span>
                  </button>
                </HideInWorkshop>
              )}
            </nav>
          </div>

          {/* Footer - Cache warm-up status for the current file */}
          <HideInWorkshop>
            <div className="border-t border-gray-200 flex-shrink-0">
              <WarmupStatusIndicator activeFile={activeFile} />
              {/* DEBUG(temp): cross-user/cross-file cache debug dump — delete this block with WarmupDebugPanel.jsx once cache warming is fixed */}
              <WarmupDebugPanel />
            </div>
          </HideInWorkshop>

          {/* Footer - Always use latest scheduled data (opt-in, per user) */}
          {user && (
            <HideInWorkshop>
              <div className="border-t border-gray-200 px-3 py-2 flex-shrink-0">
                <label
                  className={cn(
                    'flex items-center gap-2 text-sm text-gray-700 cursor-pointer',
                    isCollapsed && 'justify-center',
                  )}
                  title={t('sidebar.autoLatestData')}
                >
                  <input
                    type="checkbox"
                    checked={autoActivateLatest}
                    onChange={(e) => handleToggleAutoActivate(e.target.checked)}
                    className="h-4 w-4 flex-shrink-0"
                  />
                  {!isCollapsed && <span className="truncate">Always use latest scheduled data</span>}
                </label>
              </div>
            </HideInWorkshop>
          )}

          {/* Footer - Settings */}
          <HideInWorkshop>
            <div className="border-t border-gray-200 px-2 py-2 flex-shrink-0">
              <button
                onClick={() => setShowCacheSettings(true)}
                className="w-full flex items-center justify-start h-10 px-3 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <Settings className="h-5 w-5 mr-3 flex-shrink-0" />
                {!isCollapsed && <span className="truncate">{t('actions.settings')}</span>}
              </button>
            </div>
          </HideInWorkshop>

          {/* Footer - Account identity + Logout. Deliberately NOT wrapped in
              HideInWorkshop (bare prose, not a tag reference): workshop mode
              is fail-secure default-ON, so this is the only way a user can
              ever see who is signed in or sign out. */}
          <SidebarAccountFooter
            user={user}
            onLogout={async () => { try { await logout(); } catch { /* non-fatal; 401 interceptor will redirect */ } }}
            isCollapsed={isCollapsed}
          />
        </div>
      </div>

      {/* Cache Settings Modal */}
      {showCacheSettings && (
        <CacheSettings
          activeFile={activeFile}
          onClose={() => setShowCacheSettings(false)}
        />
      )}

      {/* Tab Filter Modal */}
      {filterModalTab && (
        <TabFilterModal
          tab={filterModalTab}
          activeFile={activeFile}
          onSave={(tabFilters) => {
            onUpdateTabFilters?.(filterModalTab.id, tabFilters);
            setFilterModalTab(null);
          }}
          onClose={() => setFilterModalTab(null)}
        />
      )}

      {/* Sandwich Menu Button */}
      <button
        ref={toggleBtnRef}
        onClick={toggleSidebar}
        className={cn(
          'fixed top-4 z-50 p-3 bg-white border border-gray-300 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:bg-gray-50',
          isCollapsed ? 'left-4' : 'left-[272px]',
        )}
        aria-label={isCollapsed ? t('sidebar.openMenu') : t('sidebar.closeMenu')}
      >
        {isCollapsed ? (
          <Menu className="h-5 w-5 text-gray-700" />
        ) : (
          <X className="h-5 w-5 text-gray-700" />
        )}
      </button>
    </>
  );
};

export default AppSidebar;
